import React from "react";
import { Input, Form, Row, Col, message, Select, Dropdown } from 'antd';
import { ExportOutlined } from "@ant-design/icons";
import XLSX from "xlsx";
import { getLangList } from '@/lib/i18n';
import {
  getPagination,
  getPageParamObject,
  getDataArray,
  getOptionArray,
  selectFilterOption,
} from "@/lib/antdElementFunction";
import { ReButton, ReRangePicker } from '@/components/refineElement';
import ResponsiveTable from '@/components/responsiveTable';
import SaleConfig from "@/config/sale";
import { getStorage } from "@/lib/webStorage";
import { startTimeToday, endTimeToday, convertToTimestamp10, dateFormat } from "@/lib/dateTime";
import { openDownloadDialog, sheet2blob, fileNameFormatTime } from "@/lib/exlsDownload";
import { hasPermission } from "@/lib/permissionUtil";
import "./index.less";

const {
  getSaleHistory,
  exportSaleHistory,
  getSaleGroupInfo,
  getBalanceInfo,
  getAllSaleSubjectInfo,
  getAllUserBalance,
} = SaleConfig;

interface state {
  pageSize: number,
  pageNo: number,
  saleHistoryObject: any,
  categoryArray: any,
  saleSubjectArray: any,
  selectedGroupId: string,
}

class BuyRecord extends React.Component<{}, state> {
  state = {
    pageSize: 20,
    pageNo: 1,
    saleHistoryObject: {},
    categoryArray: [] as never as any,
    saleSubjectArray: [] as never as any,
    selectedGroupId: '',
  };
  buttonT: any = getLangList("button");
  buyRecordT: any = getLangList("buyRecord");
  saleT: any = getLangList("sale");
  errorT: any = getLangList("error");
  formRef: any = React.createRef();

  componentDidMount() {
    this.getSaleGroupInfo();
    this.getAllSaleSubjectInfo();
    !hasPermission('BUY_RECORD_QUERY_ALL') && this.getSaleHistory();
  }

  columns = [
    { title: this.buyRecordT.ID, dataIndex: "username" },
    { title: this.buyRecordT.ACCESS_CARD_NUMBER, dataIndex: "accessCardNumber" },
    {
      title: this.buyRecordT.NAME,
      dataIndex: "nameCn",
      render: (text: any, record: any) => `${record.nameCn}-${record.nameEn}`,
    },
    { title: this.buyRecordT.TIME, dataIndex: "purchaseDate" },
    { title: this.buyRecordT.SUBJECT, dataIndex: "saleSubjectName" },
    { title: this.buyRecordT.BUY_ITEM, dataIndex: "itemName" },
    { title: this.buyRecordT.UNIT_PRICE, dataIndex: "purchaseSinglePrice" },
    { title: this.buyRecordT.QUANTITY, dataIndex: "purchaseQuantity" },
    { title: this.buyRecordT.TOTAL, dataIndex: "purchaseTotalAmount" },
  ];

  excelColumns: any = {
    N: [ // 一般匯出
      this.buyRecordT.ID,
      this.buyRecordT.ACCESS_CARD_NUMBER,
      this.buyRecordT.NAME,
      this.buyRecordT.TIME,
      this.buyRecordT.SUBJECT,
      this.buyRecordT.BUY_ITEM,
      this.buyRecordT.UNIT_PRICE,
      this.buyRecordT.QUANTITY,
      this.buyRecordT.TOTAL,
    ],
    P: [ // 以人匯出
      this.buyRecordT.ID,
      this.buyRecordT.ACCESS_CARD_NUMBER,
      this.buyRecordT.NAME,
      this.buyRecordT.DEPARTMENT,
      this.buyRecordT.CLASS,
      this.buyRecordT.TIME,
      this.buyRecordT.BUY_ITEM,
      this.buyRecordT.UNIT_PRICE,
      this.buyRecordT.QUANTITY,
      this.buyRecordT.TOTAL,
    ],
    S: [ // 以分類科目匯出
      this.buyRecordT.SUBJECT,
      this.buyRecordT.BUY_ITEM,
      this.buyRecordT.TIME,
      this.buyRecordT.UNIT_PRICE,
      this.buyRecordT.QUANTITY,
      this.buyRecordT.TOTAL,
    ],
    B: [ // 匯出所有帳號餘額
      this.buyRecordT.ID,
      this.buyRecordT.ACCESS_CARD_NUMBER,
      this.buyRecordT.NAME,
      this.buyRecordT.BALANCE,
    ]
  };

  getSaleGroupInfo = () => {
    getSaleGroupInfo().then((res: any) => {
      const categoryArray = res?.value ?? [];
      this.setState({ categoryArray });
    });
  }

  getAllSaleSubjectInfo = () => {
    getAllSaleSubjectInfo().then((res: any) => {
      const saleSubjectArray = res?.value ?? [];
      this.setState({ saleSubjectArray });
    });
  }

  getBalanceInfo = () => {
    const { username, accessCardNumber } = this.formRef.current.getFieldsValue();
    const usernameValue = hasPermission('BUY_RECORD_QUERY_ALL') ? username : getStorage("account");
    const params: any = {
      accessCardNumber,
      username: usernameValue,
    };

    getBalanceInfo(params).then((res: any) => {
      const { balance = 0 } = res?.value ?? {};

      this.formRef.current.setFieldsValue({ balance });
    });
  }

  getSaleHistory = (pagination?: any, filters?: any, sorter?: any) => {
    const { pageSize } = this.state;
    const { username, accessCardNumber, date, saleGroupId, saleSubjectId } = this.formRef.current.getFieldsValue();
    const pageParamObject = getPageParamObject(pageSize, pagination, sorter);
    const params = {
      username: hasPermission('BUY_RECORD_QUERY_ALL') ? username : getStorage("account"),
      accessCardNumber,
      startTime: convertToTimestamp10(date[0]),
      endTime: convertToTimestamp10(date[1]),
      size: pageParamObject.nowPageSize,
      page: pageParamObject.nowPageNo - 1,
      saleGroupId,
      saleSubjectId,
    };

    if (!hasPermission('BUY_RECORD_QUERY_ALL') || username || accessCardNumber) {
      this.getBalanceInfo();
    } else {
      this.formRef.current.setFieldsValue({ balance: '' });
    }

    getSaleHistory(params).then((res: any) => {
      const saleHistoryObject = res?.value ?? {};

      this.setState({
        pageSize: pageParamObject.nowPageSize,
        pageNo: pageParamObject.nowPageNo,
        saleHistoryObject,
      });
    });
  }

  getAllUserBalanceFunction = (key: string) => {
    getAllUserBalance().then((res: any) => {
      if (res.success) {
        const { value = [] } = res ?? {};
        const data = this.getExcelData(value, key);
        const sheet = XLSX.utils.aoa_to_sheet(data);
        openDownloadDialog(
          sheet2blob(sheet, "xlsx"),
          fileNameFormatTime(`${this.buyRecordT.BALANCE}`),
        );
        message.success(this.errorT.success);
      }
    });
  }

  exportSaleHistoryFunction = (key: string) => {
    const { categoryArray } = this.state;
    const { username, accessCardNumber, date, saleGroupId, saleSubjectId } = this.formRef.current.getFieldsValue();
    const params = {
      username: hasPermission('BUY_RECORD_QUERY_ALL') ? username : getStorage("account"),
      accessCardNumber,
      startTime: convertToTimestamp10(date[0]),
      endTime: convertToTimestamp10(date[1]),
      saleGroupId,
      saleSubjectId,
      exportType: key,
    };
    const saleGroupName = categoryArray?.find((item: any) => item.saleGroupId === saleGroupId)?.saleGroupName ?? '全部';

    exportSaleHistory(params).then((res: any) => {
      if (res.success) {
        const { histories = [], subjectHistories = [] } = res?.value ?? {};
        const historyArray = (key === 'S' ? subjectHistories : histories) ?? [];
        const data = this.getExcelData(historyArray, key, date);
        const sheet = XLSX.utils.aoa_to_sheet(data);
        openDownloadDialog(
          sheet2blob(sheet, "xlsx"),
          fileNameFormatTime(`${this.buyRecordT.EXCEL_NAME.MENU_TITLE}-${saleGroupName}`),
        );
        message.success(this.errorT.success);
      }
    });
  }

  download = (e: any) => {
    const { key } = e;

    switch (key) {
      case 'B':
        return this.getAllUserBalanceFunction(key);
      case 'N':
      case 'P':
      case 'S':
        return this.exportSaleHistoryFunction(key);
      default:
        return;
    }
  };

  getExcelData = (historyArray: any, exportType: string, date?: any) => {
    const exportTypeN = () => {
      const data: any = [this.excelColumns.N];

      historyArray.forEach((item: any) => {
        const row = [
          item.username,
          item.accessCardNumber,
          `${item.nameCn}-${item.nameEn}`,
          item.purchaseDate,
          item.saleSubjectName,
          item.itemName,
          item.purchaseSinglePrice,
          item.purchaseQuantity,
          item.purchaseTotalAmount,
        ];
        data.push(row);
      });

      return data;
    }

    const exportTypeP = () => {
      const data: any = [this.excelColumns.P];
      const filterDataObject: any = {};

      // filterDataObject
      historyArray.forEach((item: any) => {
        const key = item.username;

        if (filterDataObject[key]) {
          filterDataObject[key].push(item);
        } else {
          filterDataObject[key] = [item];
        }
      });

      Object.entries(filterDataObject).forEach(([key, value]: any) => {
        let onePeopleTotal = 0;
        const onePeopleObjectArray = value.map((item: any) => {
          onePeopleTotal += item.purchaseTotalAmount;

          return [
            item.username,
            item.accessCardNumber,
            `${item.nameCn}-${item.nameEn}`,
            item.departmentName,
            item.className,
            item.purchaseDate,
            item.itemName,
            item.purchaseSinglePrice,
            item.purchaseQuantity,
            item.purchaseTotalAmount,
          ]
        });
        data.push(
          ...onePeopleObjectArray,
          ['', '', '', '', '', '', '', '', onePeopleTotal],
        );
      });

      return data;
    }

    const exportTypeS = () => {
      const data: any = [[`${date[0].format(dateFormat)} ~ ${date[1].format(dateFormat)}`], this.excelColumns.S];
      const filterDataObject: any = {};

      // filterDataObject
      historyArray.forEach((item: any) => {
        const key = item.saleSubjectId;

        if (filterDataObject[key]) {
          filterDataObject[key].push(item);
        } else {
          filterDataObject[key] = [item];
        }
      });

      // 塞入對應exportType的資料
      Object.entries(filterDataObject).forEach(([key, value]: any) => {
        let oneSubjectTotal = 0;
        const onePeopleObjectArray = value.map((item: any) => {
          oneSubjectTotal += item.purchaseTotalAmount;

          return [
            item.saleSubjectName,
            item.itemName,
            item.purchaseDate,
            item.purchaseSinglePrice,
            item.purchaseQuantity,
            item.purchaseTotalAmount,
          ]
        });
        data.push(
          ...onePeopleObjectArray,
          ['', '', '', '', '', oneSubjectTotal],
        );
      });

      return data;
    }

    const exportTypeB = () => {
      const data: any = [this.excelColumns.B];

      historyArray.forEach((item: any) => {
        const row = [
          item.username,
          item.accessCardNumber,
          `${item.nameCn}-${item.nameEn}`,
          item.balance,
        ];
        data.push(row);
      });

      return data;
    }

    switch (exportType) {
      case 'N':
        return exportTypeN();
      case 'P':
        return exportTypeP();
      case 'S':
        return exportTypeS();
      case 'B':
        return exportTypeB();
      default:
        return [];
    }
  };

  render() {
    const { pageSize, pageNo, saleHistoryObject, categoryArray, saleSubjectArray, selectedGroupId }: any = this.state;
    const { histories = [], totalElements } = saleHistoryObject;
    const filterSaleSubjectArray = saleSubjectArray.filter((item: any) => `${item.saleGroupId}` === selectedGroupId);

    return (
      <>
        <Row gutter={[12, 48]}>
          <Col span={24}>
            <Form
              layout={"inline"}
              className="refine-form"
              initialValues={{
                username: '',
                accessCardNumber: '',
                balance: '',
                date: [startTimeToday, endTimeToday],
                saleGroupId: '',
                saleSubjectId: '',
              }}
              onFinish={() => this.getSaleHistory()}
              ref={this.formRef}
            >
              {hasPermission('BUY_RECORD_QUERY_ALL') && (
                <>
                  <Form.Item label={this.buyRecordT.ID} name="username">
                    <Input />
                  </Form.Item>

                  <Form.Item label={this.buyRecordT.ACCESS_CARD_NUMBER} name="accessCardNumber">
                    <Input />
                  </Form.Item>
                </>
              )}

              <Form.Item name="saleGroupId" label={this.saleT.CATEGORY}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "300px" }}
                  optionFilterProp="children"
                  filterOption={(input: any, option: any) => selectFilterOption(input, option)}
                  onChange={(activeKey: string) => this.setState({ selectedGroupId: `${activeKey}` })}
                >
                  {getOptionArray(getDataArray(categoryArray, 'saleGroupId', 'saleGroupName', true))}
                </Select>
              </Form.Item>

              <Form.Item name="saleSubjectId" label={this.saleT.SUBJECT}>
                <Select
                  showSearch
                  allowClear
                  style={{ width: "300px" }}
                  optionFilterProp="children"
                  filterOption={(input: any, option: any) => selectFilterOption(input, option)}
                >
                  {getOptionArray(getDataArray(filterSaleSubjectArray, 'saleSubjectId', 'saleSubjectName', true))}
                </Select>
              </Form.Item>

              <Form.Item label={this.buyRecordT.DATE} name="date">
                <ReRangePicker format="dateFormat" />
              </Form.Item>

              <Form.Item label={this.buyRecordT.BALANCE} name="balance">
                <Input bordered={false} disabled className="buy-record-disabled-input" />
              </Form.Item>

              <div className="re-button-container-right">
                <Form.Item>
                  <ReButton iconName='search' attributeObject={{ htmlType: 'submit' }}>
                    {this.buttonT.search}
                  </ReButton>
                </Form.Item>

                {hasPermission('BUY_RECORD_EXPORT') &&
                  <Form.Item>
                    <Dropdown.Button
                      menu={{
                        items: [
                          { key: 'N', label: this.buyRecordT.EXPORT_BY_NORMAL },
                          { key: 'P', label: this.buyRecordT.EXPORT_BY_PEOPLE },
                          { key: 'S', label: this.buyRecordT.EXPORT_BY_CATEGORY_SUBJECT },
                          { key: 'B', label: this.buyRecordT.EXPORT_ALL_USER_BALANCE },
                        ],
                        onClick: this.download,
                      }}
                    >
                      <ExportOutlined />
                      {this.buttonT.EXPORT}
                    </Dropdown.Button>
                  </Form.Item>
                }
              </div>
            </Form>
          </Col>
        </Row>
        <Row gutter={[12, 11]}>
          <Col span={24}>
            <ResponsiveTable
              {...this.props}
              pagination={getPagination(pageSize, pageNo, totalElements)}
              dataSource={histories ?? []}
              pageNoFun={this.getSaleHistory}
              columns={this.columns}
            />
          </Col>
        </Row>
      </>
    );
  }
}

export default BuyRecord;